import { useState, createElement } from "react";
import { Layout, Menu } from "antd";
import {
  HomeFilled,
  IdcardFilled,
  DesktopOutlined,
  AppstoreFilled,
  SmileFilled,
  UserOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";

import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Sider } = Layout;

const CollapseButton = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    width="14"
    height="66"
    viewBox="0 0 14 66"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
  >
    <path d="M0 0L14 8V58L0 66V0Z" fill="white" />
    <path
      d="M9.77123 32.6853L4.13623 28.284C4.1215 28.2725 4.10381 28.2652 4.08517 28.2633C4.06654 28.2613 4.04772 28.2646 4.03088 28.2728C4.01404 28.281 3.99986 28.2938 3.98998 28.3097C3.98009 28.3257 3.97489 28.3441 3.97498 28.3628V29.329C3.97498 29.3903 4.00373 29.449 4.05123 29.4865L8.55123 33.0003L4.05123 36.514C4.00248 36.5515 3.97498 36.6103 3.97498 36.6715V37.6378C3.97498 37.7215 4.07123 37.7678 4.13623 37.7165L9.77123 33.3153C9.81912 33.2779 9.85786 33.2301 9.88451 33.1756C9.91116 33.121 9.92501 33.061 9.92501 33.0003C9.92501 32.9396 9.91116 32.8796 9.88451 32.825C9.85786 32.7704 9.81912 32.7227 9.77123 32.6853Z"
      fill="black"
      fillOpacity="0.45"
      transform={collapsed ? "rotate(180 7 33)" : ""}
    />
  </svg>
);

const MENU_ITEMS = (t: (t: string) => string): MenuProps["items"] => [
  {
    key: "/dashboard",
    icon: createElement(HomeFilled),
    label: t("sidebar.dashboard"),
  },
  {
    key: "/members",
    icon: createElement(IdcardFilled),
    label: t("sidebar.members"),
  },
  {
    key: "/app-manager.menu",
    icon: createElement(AppstoreFilled),
    label: t("sidebar.appManager.menu"),
    children: [
      {
        key: "/app-manager",
        label: t("sidebar.appManager.notice"),
      },
      {
        key: "/schedule",
        label: t("sidebar.appManager.schedule"),
      },
    ],
  },
  {
    key: "/attendance.menu",
    icon: createElement(IdcardFilled),
    label: t("sidebar.attendance.menu"),
    children: [
      {
        key: "/attendance",
        label: t("sidebar.attendance.show"),
      },
      {
        key: "/attendance-manager",
        label: t("sidebar.attendance.manager"),
      },
    ],
  },
  {
    key: "/front-desk",
    icon: createElement(DesktopOutlined),
    label: t("sidebar.frontDesk"),
  },
  {
    key: "/gym-info.menu",
    icon: createElement(SmileFilled),
    label: t("sidebar.gymInfo.menu"),
    children: [
      {
        key: "/gym-info",
        label: t("sidebar.gymInfo.show"),
      },
      {
        key: "/gym-class",
        label: t("sidebar.gymInfo.gymClass"),
      },
    ],
  },
  {
    key: "/manager",
    icon: createElement(UserOutlined),
    label: t("sidebar.manager"),
  },
  {
    key: "/inquiry",
    icon: createElement(QuestionCircleOutlined),
    label: t("sidebar.inquiry"),
  },
];

const CustomSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Sider
      width={200}
      style={{
        boxShadow: "2px 2px 8px 0px rgba(0, 0, 0, 0.15)",
        position: "relative",
        zIndex: 1,
      }}
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
    >
      <Menu
        mode="inline"
        defaultSelectedKeys={["1"]}
        style={{ height: "100%", borderRight: 0 }}
        items={MENU_ITEMS(t)}
        onClick={({ key }) => {
          if (key.includes("/")) {
            navigate(key);
          }
        }}
      />
      <button
        type="button"
        className="absolute w-3.5 h-[66px] -right-3.5 top-1/2 -translate-y-1/2 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
        style={{
          filter: "drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.15))",
        }}
        aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
      >
        <CollapseButton collapsed={collapsed} />
      </button>
    </Sider>
  );
};

export default CustomSidebar;
